/**
 * Rule-based group Traffic Distribution AI helpers (group-scoped automationConfiguration).
 *
 * Mirrors `default-paths-optimization` for `conditionalPathsGroups[]` items.
 * Voluum reads the on/off + calculation method from `group.automationConfiguration`,
 * not from the per-path `autoOptimized` flag (which is a separate per-path
 * offer/lander optimization control).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  VoluumCalculationMethod,
  VoluumPathGroupUpdate,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import {
  getSetAutoOptimized,
  getSetCalculationMethod,
  setSetAutoOptimized,
  setSetCalculationMethod,
} from '../utils/set-automation';

export type GroupOptimizationUniform = 'allOn' | 'allOff' | 'mixed';

export interface GroupOptimizationStats {
  total: number;
  enabled: number;
}

export function groupOptimizationStats(
  group: VoluumPathGroupUpdate,
): GroupOptimizationStats {
  const total = group.paths?.length ?? 0;
  if (total === 0) return { total: 0, enabled: 0 };
  return { total: 1, enabled: getSetAutoOptimized(group) ? 1 : 0 };
}

export function groupOptimizationUniform(
  group: VoluumPathGroupUpdate,
): GroupOptimizationUniform {
  if (!group.paths || group.paths.length === 0) return 'allOff';
  return getSetAutoOptimized(group) ? 'allOn' : 'allOff';
}

export function groupCalculationMethodUniform(
  group: VoluumPathGroupUpdate,
): VoluumCalculationMethod | null {
  return getSetCalculationMethod(group);
}

export function patchEachGroupPath(
  group: VoluumPathGroupUpdate,
  mutator: (p: VoluumPathUpdate) => void,
): void {
  group.paths?.forEach(mutator);
}

export function setGroupOptimizationEnabled(
  group: VoluumPathGroupUpdate,
  enabled: boolean,
): void {
  setSetAutoOptimized(group, enabled);
}

export function setGroupCalculationMethod(
  group: VoluumPathGroupUpdate,
  method: VoluumCalculationMethod,
): void {
  setSetCalculationMethod(group, method);
}
